import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { CompletionData } from '../types/analytics.types';
import './CompletionRatesChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CompletionRatesChartProps {
  data: CompletionData;
  loading?: boolean;
}

type ViewMode = 'section' | 'language';

export const CompletionRatesChart: React.FC<CompletionRatesChartProps> = ({ data, loading }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('section');

  if (loading) {
    return (
      <div className="completion-chart">
        <div className="chart-header">
          <h4>Completion Rates</h4>
        </div>
        <div className="loading-skeleton" style={{ height: '300px' }}></div>
      </div>
    );
  }

  if (!data) {
    return <div className="no-data">No completion data available</div>;
  }

  const displayData = viewMode === 'section' ? data.by_section : data.by_language;

  // Sort by completion rate and take top 10
  const sortedData = [...displayData]
    .sort((a, b) => b.completion_rate - a.completion_rate)
    .slice(0, 10);

  const labels = sortedData.map(item =>
    viewMode === 'section'
      ? (item.section_id || 'Unknown').substring(0, 20)
      : (item.language || 'Unknown')
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Completed',
        data: sortedData.map(item => item.completed),
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2
      },
      {
        label: 'In Progress',
        data: sortedData.map(item => item.in_progress),
        backgroundColor: 'rgba(255, 206, 86, 0.8)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          afterLabel: function(context: any) {
            const index = context.dataIndex;
            const item = sortedData[index];
            return [
              `Total: ${item.total_enrollments}`,
              `Completion Rate: ${item.completion_rate.toFixed(1)}%`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  return (
    <div className="completion-chart">
      <div className="chart-header">
        <h4>Completion Rates - Top Performers</h4>
        <div className="view-mode-selector">
          <button
            className={viewMode === 'section' ? 'active' : ''}
            onClick={() => setViewMode('section')}
          >
            By Section
          </button>
          <button
            className={viewMode === 'language' ? 'active' : ''}
            onClick={() => setViewMode('language')}
          >
            By Language
          </button>
        </div>
      </div>
      <div className="chart-wrapper" style={{ height: '300px' }}>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <div className="chart-summary">
        <p>
          Overall completion rate: <strong>{data.overall_completion_rate.toFixed(1)}%</strong>
          {data.avg_time_to_completion_days > 0 && (
            <> · Avg time to complete: <strong>{data.avg_time_to_completion_days.toFixed(1)} days</strong></>
          )}
        </p>
      </div>
    </div>
  );
};

export default CompletionRatesChart;
